import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { AppState } from '../app.state';
import { catchError, exhaustMap, share, switchMap, withLatestFrom } from 'rxjs/operators';
import { SharedActions } from '@states/shared/shared.action-types';
import { SyncQueueActions } from '@states/sync-queue/sync-queue.action-types';
import { SyncQueueService } from '../../services/sync-queue.service';
import * as SyncQueueSelectors from '@states/sync-queue/sync-queue.selectors';

@Injectable()
export class SyncQueueEffects {
  public startGetCommittedOperations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SyncQueueActions.startGetCommittedOperations),
      exhaustMap(() => [
        SyncQueueActions.setSyncQueueLoading({ isLoading: true }),
        SyncQueueActions.getCommittedOperations(),
      ]),
    ),
  );

  public getCommittedOperations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SyncQueueActions.getCommittedOperations),
      withLatestFrom(this.store$.pipe(select(state => state.edgeEditState))),
      switchMap(([, { selectedEdgeId }]) => {
        return this.syncQueueService.getLastCommittedOperations(selectedEdgeId, 0)
          .pipe(
            switchMap(response => [
              SyncQueueActions.getCommittedOperationsSuccess({ items: response.items, total: response.total, page: 0 }),
              SyncQueueActions.setSyncQueueLoading({ isLoading: false }),
            ]),
            catchError(error => [
              SyncQueueActions.getCommittedOperationsFail(),
              SharedActions.showMessage({ error: 'Failed to load committed operations' }),
              SyncQueueActions.setSyncQueueLoading({ isLoading: false }),
            ]),
          );
      }),
      share(),
    ),
  );

  getEdgesSyncStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SyncQueueActions.getEdgesSyncStatus),
      switchMap(() => {
        return this.syncQueueService.isEdgeSyncForOrg()
          .pipe(
            switchMap(edgesSyncStatus => [
              SyncQueueActions.getEdgesSyncStatusSuccess({ edgesSyncStatus }),
            ]),
            catchError(error => [
              SyncQueueActions.getEdgesSyncStatusFail(),
              SharedActions.consoleMessage({ error: 'Failed to load edges sync status' }),
            ]),
          );
      }),
      share(),
    ),
  );

  validateEdgeSync$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SyncQueueActions.validateEdgeSync),
      withLatestFrom(this.store$.pipe(select(state => state.edgeEditState))),
      switchMap(([{ edgeId }, { selectedEdgeId }]) => {
        const targetEdgeId = edgeId || selectedEdgeId;
        return this.syncQueueService.validateEdgeSync(targetEdgeId)
          .pipe(
            switchMap(syncValidation => [
              SyncQueueActions.validateEdgeSyncSuccess({ syncValidation }),
              SharedActions.showMessage({
                success: syncValidation.isSync
                  ? 'Edge and cloud are in sync!'
                  : 'Edge and cloud are out of sync.'
              }),
            ]),
            catchError(error => [
              SyncQueueActions.validateEdgeSyncFail(),
              SharedActions.showMessage({ error: 'Failed to validate edge sync status' }),
            ]),
          );
      }),
      share(),
    ),
  );

  public startGetUncommittedOperations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SyncQueueActions.startGetUncommittedOperations),
      exhaustMap(() => [
        SyncQueueActions.setSyncQueueLoading({ isLoading: true }),
        SyncQueueActions.getUncommittedOperations(),
      ]),
    ),
  );

  public getUncommittedOperations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SyncQueueActions.getUncommittedOperations),
      withLatestFrom(this.store$.pipe(select(state => state.edgeEditState))),
      switchMap(([, { selectedEdgeId }]) => {
        return this.syncQueueService.getNextOperations(selectedEdgeId, 0)
          .pipe(
            switchMap(response => [
              SyncQueueActions.getUncommittedOperationsSuccess({ items: response.items, total: response.total, page: 0 }),
              SyncQueueActions.setSyncQueueLoading({ isLoading: false }),
            ]),
            catchError(error => [
              SyncQueueActions.getUncommittedOperationsFail(),
              SharedActions.showMessage({ error: 'Failed to load uncommitted operations' }),
              SyncQueueActions.setSyncQueueLoading({ isLoading: false }),
            ]),
          );
      }),
      share(),
    ),
  );

  public startGetErrorOrSkippedOperations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SyncQueueActions.startGetErrorOrSkippedOperations),
      exhaustMap(() => [
        SyncQueueActions.setSyncQueueLoading({ isLoading: true }),
        SyncQueueActions.getErrorOrSkippedOperations(),
      ]),
    ),
  );

  public getErrorOrSkippedOperations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SyncQueueActions.getErrorOrSkippedOperations),
      withLatestFrom(this.store$.pipe(select(state => state.edgeEditState))),
      switchMap(([, { selectedEdgeId }]) => {
        return this.syncQueueService.getErrorOrSkippedOperations(selectedEdgeId, 0)
          .pipe(
            switchMap(response => [
              SyncQueueActions.getErrorOrSkippedOperationsSuccess({ items: response.items, total: response.total, page: 0 }),
              SyncQueueActions.setSyncQueueLoading({ isLoading: false }),
            ]),
            catchError(error => [
              SyncQueueActions.getErrorOrSkippedOperationsFail(),
              SharedActions.showMessage({ error: 'Failed to load error/skipped operations' }),
              SyncQueueActions.setSyncQueueLoading({ isLoading: false }),
            ]),
          );
      }),
      share(),
    ),
  );

  retryOperation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SyncQueueActions.retryOperation),
      withLatestFrom(this.store$.pipe(select(state => state.edgeEditState))),
      switchMap(([{ edgeLogId }, { selectedEdgeId }]) => {
        return this.syncQueueService.retryOperation(selectedEdgeId, edgeLogId)
          .pipe(
            switchMap(() => [
              SyncQueueActions.retryOperationSuccess(),
              SyncQueueActions.startGetUncommittedOperations(),
              SharedActions.showMessage({ success: 'The operation has been submitted for another attempt' }),
            ]),
            catchError(error => [
              SyncQueueActions.retryOperationFail(),
              SharedActions.showMessage({ error: 'Failed to retry operation' }),
            ]),
          );
      }),
    ),
  );

  cancelOperation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SyncQueueActions.cancelOperation),
      withLatestFrom(this.store$.pipe(select(state => state.edgeEditState))),
      switchMap(([{ edgeLogId }, { selectedEdgeId }]) => {
        return this.syncQueueService.cancelOperation(selectedEdgeId, edgeLogId)
          .pipe(
            switchMap(() => [
              SyncQueueActions.cancelOperationSuccess(),
              SyncQueueActions.startGetUncommittedOperations(),
              SharedActions.showMessage({ success: 'Operation cancelled successfully' }),
            ]),
            catchError(error => [
              SyncQueueActions.cancelOperationFail(),
              SharedActions.showMessage({ error: 'Failed to cancel operation' }),
            ]),
          );
      }),
    ),
  );
  resetPublishCounter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SyncQueueActions.resetPublishCounter),
      withLatestFrom(this.store$.pipe(select(state => state.edgeEditState))),
      switchMap(([{ edgeLogId }, { selectedEdgeId }]) => {
        return this.syncQueueService.resetPublishCounter(selectedEdgeId, edgeLogId)
          .pipe(
            switchMap(() => [
              SyncQueueActions.resetPublishCounterSuccess(),
              SyncQueueActions.startGetUncommittedOperations(),
              SharedActions.showMessage({ success: 'Publish counter reset successfully' }),
            ]),
            catchError(error => [
              SyncQueueActions.resetPublishCounterFail(),
              SharedActions.showMessage({ error: 'Failed to reset publish counter' }),
            ]),
          );
      }),
    ),
  );

  public loadMoreCommittedOperations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SyncQueueActions.loadMoreCommittedOperations),
      withLatestFrom(
        this.store$.pipe(select(state => state.edgeEditState)),
        this.store$.pipe(select(SyncQueueSelectors.selectCommittedPage)),
      ),
      switchMap(([, { selectedEdgeId }, currentPage]) => {
        const nextPage = currentPage + 1;
        return this.syncQueueService.getLastCommittedOperations(selectedEdgeId, nextPage)
          .pipe(
            switchMap(response => [
              SyncQueueActions.getCommittedOperationsSuccess({ items: response.items, total: response.total, page: nextPage }),
            ]),
            catchError(error => [
              SyncQueueActions.getCommittedOperationsFail(),
              SharedActions.showMessage({ error: 'Failed to load more committed operations' }),
            ]),
          );
      }),
      share(),
    ),
  );

  public loadMoreUncommittedOperations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SyncQueueActions.loadMoreUncommittedOperations),
      withLatestFrom(
        this.store$.pipe(select(state => state.edgeEditState)),
        this.store$.pipe(select(SyncQueueSelectors.selectUncommittedPage)),
      ),
      switchMap(([, { selectedEdgeId }, currentPage]) => {
        const nextPage = currentPage + 1;
        return this.syncQueueService.getNextOperations(selectedEdgeId, nextPage)
          .pipe(
            switchMap(response => [
              SyncQueueActions.getUncommittedOperationsSuccess({ items: response.items, total: response.total, page: nextPage }),
            ]),
            catchError(error => [
              SyncQueueActions.getUncommittedOperationsFail(),
              SharedActions.showMessage({ error: 'Failed to load more uncommitted operations' }),
            ]),
          );
      }),
      share(),
    ),
  );

  public loadMoreErrorOrSkippedOperations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SyncQueueActions.loadMoreErrorOrSkippedOperations),
      withLatestFrom(
        this.store$.pipe(select(state => state.edgeEditState)),
        this.store$.pipe(select(SyncQueueSelectors.selectErrorOrSkippedPage)),
      ),
      switchMap(([, { selectedEdgeId }, currentPage]) => {
        const nextPage = currentPage + 1;
        return this.syncQueueService.getErrorOrSkippedOperations(selectedEdgeId, nextPage)
          .pipe(
            switchMap(response => [
              SyncQueueActions.getErrorOrSkippedOperationsSuccess({ items: response.items, total: response.total, page: nextPage }),
            ]),
            catchError(error => [
              SyncQueueActions.getErrorOrSkippedOperationsFail(),
              SharedActions.showMessage({ error: 'Failed to load more error/skipped operations' }),
            ]),
          );
      }),
      share(),
    ),
  );

  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private syncQueueService: SyncQueueService,
  ) {
  }
}
